// Vocabulario y helpers puros de Administración · Registro de cambios
// (T-qtbl). Sin JSX ni hooks: la pantalla los compone y los tests los
// prueban solos.
//
// El texto «Cambio» (`summary`) llega armado del servidor; aquí sólo se
// nombran la sección (con las palabras de la receta), quién lo hizo
// (nombre del equipo si lo tenemos, si no el correo) y cuándo (fecha y
// hora cortas, siempre en hora de Chile, como el CSV del servidor).

import type { ConfigAuditItem, ConfigAuditSection, OrgMember } from "@bitacorapro/api-types";
import type { AdminWords } from "../../../lib/admin/words";
import { todayIsoDate } from "../../../lib/csv";

// Los chips del prototipo (sec-nuevas.jsx SecRegistro): Todo · Equipo ·
// Contexto IA · {Activos} · Checklists, con esos textos («Equipo» a
// secas; el título largo del menú queda para la columna Sección). El
// resto de secciones no tiene chip propio y sólo aparece bajo «Todo».
export type RegistroFiltro = {
  key: "todo" | ConfigAuditSection;
  /** undefined = sin parámetro `section`. */
  section: ConfigAuditSection | undefined;
  label: string;
  /** Cómo se nombra la sección dentro del title del chip («…los cambios
   *  de {titleNoun}»): escrito a mano y no derivado del label, para que
   *  la sigla de «Contexto IA» no termine en minúsculas. */
  titleNoun: string;
};

export function registroFiltros(words: AdminWords, assetPlural: string): RegistroFiltro[] {
  return [
    { key: "todo", section: undefined, label: "Todo", titleNoun: "todas las secciones" },
    { key: "equipo", section: "equipo", label: "Equipo", titleNoun: "equipo" },
    {
      key: "contexto_ia",
      section: "contexto_ia",
      label: seccionLabel("contexto_ia", words),
      titleNoun: "contexto IA",
    },
    {
      key: "activos",
      section: "activos",
      label: assetPlural,
      titleNoun: assetPlural.toLocaleLowerCase("es-CL"),
    },
    {
      key: "checklists",
      section: "checklists",
      label: words.sections.checklists,
      titleNoun: words.sections.checklists.toLocaleLowerCase("es-CL"),
    },
  ];
}

/** El resultado de useInfiniteQuery tal como lo lee QueryBoundary:
 *  `isFetching` se enciende también mientras «Cargar más» trae la página
 *  siguiente, y useQueryView lo tomaría por una revalidación de fondo
 *  («Actualizando…» sobre la tabla) encima del «Cargando…» del botón.
 *  La página siguiente ya tiene su indicador; aquí se le quita al
 *  boundary. */
export function vistaRegistro<Q extends { isFetching: boolean; isFetchingNextPage: boolean }>(
  q: Q
): Q {
  return { ...q, isFetching: q.isFetching && !q.isFetchingNextPage };
}

// La columna Sección con el título que esa sección tiene en el menú
// (adminWords), para que «Tiendas y zonas» se llame igual en los dos
// lados. `contexto_ia` y `garantias` no son secciones del menú hoy.
export function seccionLabel(section: ConfigAuditSection, words: AdminWords): string {
  switch (section) {
    case "contexto_ia":
      return "Contexto IA";
    case "garantias":
      return "Garantías";
    default:
      return words.sections[section];
  }
}

/** Nombre visible de quien hizo el cambio: el del equipo si el correo
 *  está en el roster, si no el correo tal cual; «Sistema» cuando el
 *  evento no trae a nadie. Para soporte la pantalla pinta la pill y no
 *  llama aquí. */
export function quienLabel(
  actor: ConfigAuditItem["actor"],
  rosterByEmail: ReadonlyMap<string, Pick<OrgMember, "displayName">>
): string {
  if (!actor.name) return "Sistema";
  return rosterByEmail.get(actor.name.toLowerCase())?.displayName ?? actor.name;
}

export function rosterPorCorreo(
  members: ReadonlyArray<Pick<OrgMember, "email" | "displayName">> | undefined
): ReadonlyMap<string, Pick<OrgMember, "displayName">> {
  const map = new Map<string, Pick<OrgMember, "displayName">>();
  for (const m of members ?? []) map.set(m.email.toLowerCase(), m);
  return map;
}

// «30 oct, 11:02» en hora de Chile, armado por partes: es-CL separa día y
// mes con guión y pone «a m.» si se le deja la hora de 12. Zona fija: el
// gerente lee el registro desde donde esté y el CSV del servidor ya usa
// Santiago.
const CUANDO = new Intl.DateTimeFormat("es-CL", {
  timeZone: "America/Santiago",
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

export function fmtCuando(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const parts = new Map(CUANDO.formatToParts(d).map((p) => [p.type, p.value]));
  const month = (parts.get("month") ?? "").replace(".", "");
  return `${parts.get("day")} ${month}, ${parts.get("hour")}:${parts.get("minute")}`;
}

/** El texto de un Blob. `Blob.text()` cuando existe; si no (navegadores
 *  viejos, jsdom), por FileReader. */
export function leerTextoBlob(blob: Blob): Promise<string> {
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new Error("No se pudo leer el archivo"));
    reader.readAsText(blob);
  });
}

/** Filas de datos de un CSV RFC 4180: registros menos la cabecera.
 *  Cuenta saltos de línea fuera de comillas, así un «Cambio» con salto
 *  interno sigue siendo una fila. BOM y CRLF final no suman. */
export function contarFilasCsv(text: string): number {
  const body = text.startsWith("﻿") ? text.slice(1) : text;
  if (body.trim().length === 0) return 0;
  let records = 0;
  let inQuotes = false;
  let pending = false;
  for (const ch of body) {
    if (ch === '"') {
      inQuotes = !inQuotes;
      pending = true;
    } else if (ch === "\n" && !inQuotes) {
      records += 1;
      pending = false;
    } else if (ch !== "\r") {
      pending = true;
    }
  }
  if (pending) records += 1;
  return Math.max(0, records - 1);
}

/** El nombre del archivo: el que mandó el servidor (Content-Disposition)
 *  y, si no vino, `registro-cambios-<fecha>.csv` con la fecha de Chile. */
export function nombreArchivoCsv(serverFilename: string | null, at: Date = new Date()): string {
  return serverFilename ?? `registro-cambios-${todayIsoDate(at)}.csv`;
}

export function filasLabel(n: number): string {
  return n === 1 ? "1 fila" : `${n} filas`;
}
